import { createClient, User, AuthChangeEvent, Session } from '@supabase/supabase-js';
import { Team, TeamInsert, Order, Kit, KitInsert, TeamInvitation, TeamInvitationInsert, TeamMember, TeamMemberInsert, Profile, ProfileInsert } from '../types/database';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export const auth = {
  signUp: async (email: string, password: string, profileData: { username: string; full_name?: string; gender?: string; tshirt_size?: string }) => {
    console.log(`[auth email] Requesting account-confirmation email from Supabase for ${email}`);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: profileData.username,
          full_name: profileData.full_name,
          gender: profileData.gender,
          tshirt_size: profileData.tshirt_size
        }
      }
    });
    if (error) {
      console.error(`[auth email] Supabase signUp request FAILED for ${email}:`, error);
      throw error;
    }

    // Supabase returns a normal-looking success response (to prevent user enumeration) when
    // the email is already registered, but does NOT send a new confirmation email unless we
    // explicitly ask it to via resend(). An empty `identities` array is the signal for this.
    const alreadyRegistered = (data.user?.identities?.length ?? 0) === 0;
    if (alreadyRegistered) {
      const { error: resendError } = await supabase.auth.resend({ type: 'signup', email });
      if (resendError) {
        console.log(`[auth email] ${email} is already registered and resend failed (likely already confirmed):`, resendError.message);
        return { data: data.user, session: data.session, error: null, alreadyRegistered: true, alreadyConfirmed: true };
      }
      console.log(`[auth email] ${email} was already registered but unconfirmed — triggered a fresh confirmation email via resend().`);
      return { data: data.user, session: data.session, error: null, alreadyRegistered: true, alreadyConfirmed: false };
    }

    console.log(`[auth email] Supabase accepted signUp request for ${email} (user id=${data.user?.id}) — confirmation email should be sent by Supabase; check Supabase dashboard → Authentication → Logs to confirm delivery.`);
    return { data: data.user, session: data.session, error, alreadyRegistered: false, alreadyConfirmed: false };
  },

  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return { data: data.user, error };
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  getUser: async (): Promise<User | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  onAuthStateChange: (callback: (event: AuthChangeEvent, session: Session | null) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  requestPasswordReset: async (email: string) => {
    console.log(`[auth email] Requesting password-reset email from Supabase for ${email}`);
    const redirectTo = `${window.location.origin}/account`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) {
      console.error(`[auth email] Supabase resetPasswordForEmail request FAILED for ${email}:`, error);
      throw error;
    }
    console.log(`[auth email] Supabase accepted password-reset request for ${email} — email should be sent by Supabase; check Supabase dashboard → Authentication → Logs to confirm delivery.`);
    return { error: null };
  },

  updatePassword: async (newPassword: string) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return { data: data.user, error: null };
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.email) throw new Error("Not signed in.");

    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword
    });
    if (verifyError) throw new Error("Current password is incorrect.");

    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;

    await supabase.auth.signOut({ scope: 'others' });
    return { data: data.user, error: null };
  }
};

export const teams = {
  getByUser: async (userId: string) => {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('created_by', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  getById: async (teamId: string) => {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('id', teamId)
      .single();
    return { data, error };
  },

  getAllLogos: async () => {
    const { data, error } = await supabase
      .from('teams')
      .select('id, logo_url, logo_thumb_url')
      .not('logo_url', 'is', null)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  getTeamsWhereUserIsMember: async (userId: string) => {
    // Get teams where user is a member (but not creator)
    const { data, error } = await supabase
      .from('team_members')
      .select('team_id, teams(*)')
      .eq('user_id', userId);
    
    if (error) return { data: null, error };
    
    // Extract the teams from the join
    const teams = data?.map((tm: any) => tm.teams).filter(Boolean) || [];
    return { data: teams, error: null };
  },

  create: async (teamData: TeamInsert) => {
    const { data, error } = await supabase
      .from('teams')
      .insert(teamData)
      .select()
      .single();
    return { data, error };
  },

  checkNameExists: async (teamName: string): Promise<boolean> => {
    const { data } = await supabase
      .from('teams')
      .select('id')
      .eq('team_name', teamName)
      .maybeSingle();
    return !!data;
  },

  update: async (teamId: string, updates: { summary?: string; team_name?: string }) => {
    const { data, error } = await supabase
      .from('teams')
      .update(updates)
      .eq('id', teamId)
      .select()
      .single();
    return { data, error };
  },

  uploadLogoBase64: async (teamId: string, base64Data: string) => {
    const response = await fetch(base64Data);
    const blob = await response.blob();

    const filePath = `${teamId}/${teamId}.png`;
    const { error } = await supabase.storage
      .from('logos')
      .upload(filePath, blob, { contentType: 'image/png', upsert: true });

    if (error) return { data: null, error };

    const { data: { publicUrl } } = supabase.storage
      .from('logos').getPublicUrl(filePath);

    // Generate and upload thumbnail
    let thumbUrl = '';
    try {
      const thumbBlob = await new Promise<Blob>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 80;
          canvas.height = 80;
          canvas.getContext('2d')!.drawImage(img, 0, 0, 80, 80);
          canvas.toBlob(
            b => b ? resolve(b) : reject(new Error('thumb blob failed')),
            'image/webp', 0.7
          );
        };
        img.onerror = reject;
        img.src = base64Data;
      });
      const thumbPath = `${teamId}/${teamId}_thumb.webp`;
      await supabase.storage.from('logos').upload(thumbPath, thumbBlob, {
        contentType: 'image/webp', upsert: true,
      });
      thumbUrl = supabase.storage.from('logos').getPublicUrl(thumbPath).data.publicUrl;
    } catch {
      thumbUrl = publicUrl;
    }

    return { data: { logoUrl: publicUrl, thumbUrl }, error: null };
  },

  delete: async (teamId: string) => {
    // Cascade: delete storage files, invitations, members, kits, then the team

    // 1. Get kit IDs so we can clean up kit-images storage
    const { data: teamKits } = await supabase
      .from('kits')
      .select('kit_id')
      .eq('team_id', teamId);

    // 2. Delete kit images from storage
    if (teamKits && teamKits.length > 0) {
      for (const kit of teamKits) {
        const { data: kitFiles } = await supabase.storage
          .from('kit-images')
          .list(kit.kit_id);
        if (kitFiles && kitFiles.length > 0) {
          const paths = kitFiles.map((f: { name: string }) => `${kit.kit_id}/${f.name}`);
          await supabase.storage.from('kit-images').remove(paths);
        }
      }
    }

    // 3. Delete team logo from storage
    const { data: logoFiles } = await supabase.storage
      .from('logos')
      .list(teamId);
    if (logoFiles && logoFiles.length > 0) {
      const paths = logoFiles.map((f: { name: string }) => `${teamId}/${f.name}`);
      await supabase.storage.from('logos').remove(paths);
    }

    // 4. Nullify team_id on orders so they survive team deletion
    const { error: orderError } = await supabase
      .from('orders')
      .update({ team_id: null })
      .eq('team_id', teamId);
    if (orderError) {
      console.error("Failed to nullify orders team_id:", orderError);
      // Non-fatal — orders table might not have team_id column
    }

    // 5. Delete database records
    const { error: invError } = await supabase
      .from('team_invitations')
      .delete()
      .eq('team_id', teamId);
    if (invError) {
      console.error("Failed to delete invitations:", invError);
      return { error: invError };
    }

    const { error: memError } = await supabase
      .from('team_members')
      .delete()
      .eq('team_id', teamId);
    if (memError) {
      console.error("Failed to delete members:", memError);
      return { error: memError };
    }

    const { error: kitError } = await supabase
      .from('kits')
      .delete()
      .eq('team_id', teamId);
    if (kitError) {
      console.error("Failed to delete kits:", kitError);
      return { error: kitError };
    }

    const { error } = await supabase
      .from('teams')
      .delete()
      .eq('id', teamId);
    if (error) console.error("Failed to delete team:", error);
    return { error };
  }
};

export const orders = {
  getByUser: async (userEmail: string) => {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('user_email', userEmail)
      .order('created_at', { ascending: false });
    return { data, error };
  }
};

export const kits = {
  getByTeam: async (teamId: string) => {
    const { data, error } = await supabase
      .from('kits')
      .select('*')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  getById: async (kitId: string) => {
    const { data, error } = await supabase
      .from('kits')
      .select('*')
      .eq('kit_id', kitId)
      .single();
    return { data, error };
  },

  getAllPreviews: async () => {
    const { data, error } = await supabase
      .from('kits')
      .select('id, preview_front_url, created_at')
      .not('preview_front_url', 'is', null)
      .gte('created_at', '2026-05-20T10:30:00+01:00')
      .order('created_at', { ascending: false })
      .limit(30);

    // A second, narrower regression (fixed the same session) baked in an opaque
    // background for guest-flow kits created in this window too — see commits
    // a0618aa (introduced) / fd46ccb (fixed).
    const filtered = (data || []).filter((kit: any) => {
      const t = new Date(kit.created_at).getTime();
      const badStart = new Date('2026-06-15T00:15:00+01:00').getTime();
      const badEnd = new Date('2026-06-15T01:00:00+01:00').getTime();
      return t < badStart || t > badEnd;
    });

    return { data: filtered, error };
  },

  create: async (kitData: KitInsert) => {
    const { data, error } = await supabase
      .from('kits')
      .insert(kitData)
      .select()
      .single();
    return { data, error };
  },

  update: async (kitId: string, updates: Partial<Kit>) => {
    const { data, error } = await supabase
      .from('kits')
      .update(updates)
      .eq('kit_id', kitId)
      .select()
      .single();
    return { data, error };
  },

  delete: async (kitId: string) => {
    const { error } = await supabase
      .from('kits')
      .delete()
      .eq('kit_id', kitId);
    return { error };
  },

  uploadPreviewImage: async (kitId: string, imageBase64: string, view: 'front' | 'back') => {
    const response = await fetch(imageBase64);
    const blob = await response.blob();
    
    const fileName = `${kitId}_${view}.png`;
    const filePath = `${kitId}/${fileName}`;

    const { error } = await supabase.storage
      .from('kit-images')
      .upload(filePath, blob, {
        contentType: 'image/png',
        upsert: true
      });

    if (error) return { data: null, error };

    const { data: { publicUrl } } = supabase.storage
      .from('kit-images')
      .getPublicUrl(filePath);

    return { data: publicUrl, error: null };
  }
};

export const teamInvitations = {
  create: async (invitationData: TeamInvitationInsert) => {
    const { data, error } = await supabase
      .from('team_invitations')
      .insert(invitationData)
      .select()
      .single();
    return { data, error };
  },

  getByToken: async (token: string) => {
    const { data, error } = await supabase
      .from('team_invitations')
      .select('*, teams(team_name)')
      .eq('token', token)
      .single();
    return { data, error };
  },

  getByTeam: async (teamId: string) => {
    const { data, error } = await supabase
      .from('team_invitations')
      .select('*')
      .eq('team_id', teamId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  accept: async (token: string) => {
    const { data, error } = await supabase
      .from('team_invitations')
      .update({ 
        status: 'accepted', 
        accepted_at: new Date().toISOString() 
      })
      .eq('token', token)
      .select()
      .single();
    return { data, error };
  }
};

export const teamMembers = {
  create: async (memberData: TeamMemberInsert) => {
    const { data, error } = await supabase
      .from('team_members')
      .insert(memberData)
      .select()
      .single();
    return { data, error };
  },

  getByTeam: async (teamId: string) => {
    // Get team members first
    const { data: membersData, error: membersError } = await supabase
      .from('team_members')
      .select('*')
      .eq('team_id', teamId)
      .order('joined_at', { ascending: false });
    
    if (membersError) return { data: null, error: membersError };
    if (!membersData || membersData.length === 0) return { data: [], error: null };

    // Get profiles for all members
    const userIds = membersData.map((m: { user_id: string }) => m.user_id);
    const { data: profilesData, error: profilesError } = await supabase
      .from('profiles')
      .select('*')
      .in('id', userIds);
    
    if (profilesError) {
      console.error("Failed to fetch profiles:", profilesError);
      return { data: membersData as TeamMember[], error: null }; // Return members without profiles
    }

    // Combine members with their profiles
    const membersWithProfiles: TeamMember[] = membersData.map((member: any) => ({
      ...member,
      profile: profilesData?.find((p: Profile) => p.id === member.user_id) || null
    }));

    return { data: membersWithProfiles, error: null };
  },

  remove: async (memberId: string) => {
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('id', memberId);
    return { error };
  }
};

export const profiles = {
  create: async (profileData: ProfileInsert) => {
    const { data, error } = await supabase
      .from('profiles')
      .insert(profileData)
      .select()
      .single();
    return { data, error };
  },

  getById: async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },

  update: async (userId: string, updates: Partial<Profile>) => {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  }
};